import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import {
  deleteWorkpaper,
  downloadWorkpaper,
  getJob,
  listWorkpapers,
  startReview,
  saveBlob,
  startWorkpaper,
} from "../lib/api";
import type { Client, Job } from "../lib/api";
import { formatElapsed, formatWhen, formatWhenLong } from "../lib/format";
import { TrashIcon } from "./Icons";
import { WorkpaperDiff } from "./WorkpaperDiff";

const iso = (d: Date) => d.toISOString().slice(0, 10);
const shortMonth = (d: Date) => d.toLocaleString("en-AU", { month: "short", timeZone: "UTC" });

/** Quarters are the common case, so the period defaults to the last completed one. */
function lastCompletedQuarter(today = new Date()): { start: string; end: string } {
  const month = today.getUTCMonth();
  const year = today.getUTCFullYear();
  // Australian BAS quarters end Sep, Dec, Mar and Jun.
  const endMonth = Math.floor(month / 3) * 3 - 1;
  const endDate = new Date(Date.UTC(year, endMonth + 1, 0));
  const startDate = new Date(Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth() - 2, 1));
  return { start: iso(startDate), end: iso(endDate) };
}

/**
 * How the period is written on the workpaper, derived from the dates.
 *
 * Derived, never stored. It used to be set once alongside the default
 * quarter, and the date pickers changed the dates without it — so choosing
 * April to June still produced a workpaper headed "Jul–Sep 2026", and the
 * filename and the history entry agreed with the heading rather than with
 * the figures.
 */
export function labelFor(start: string, end: string): string {
  const from = new Date(`${start}T00:00:00Z`);
  const to = new Date(`${end}T00:00:00Z`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return "";

  const sameYear = from.getUTCFullYear() === to.getUTCFullYear();
  if (sameYear && from.getUTCMonth() === to.getUTCMonth()) {
    return `${shortMonth(from)} ${to.getUTCFullYear()}`;
  }
  if (sameYear) {
    return `${shortMonth(from)}–${shortMonth(to)} ${to.getUTCFullYear()}`;
  }
  // A year straddling 30 June needs both years or it reads as the wrong one.
  return `${shortMonth(from)} ${from.getUTCFullYear()}–${shortMonth(to)} ${to.getUTCFullYear()}`;
}

/** How often to ask again while a job runs. Generation takes minutes. */
const POLL_MS = 3000;

/** One BAS period, with every run made for it. */
export interface PeriodGroup {
  /** The period end, which is what the runs are ordered on. */
  end: string;
  /** How the period reads, taken from the runs themselves. */
  label: string;
  runs: Job[];
}

/**
 * A client's runs, gathered under the period each was for.
 *
 * A quarter gets regenerated repeatedly while it is being prepared -- one
 * Demo quarter has four runs and a Raytech quarter five -- so a flat list
 * mixes periods together and the question "what is the latest for Apr-Jun"
 * takes reading every row.
 *
 * Ordered by the period end rather than the label, newest period first, and
 * newest run first within each. Grouping is by period rather than by quarter
 * specifically: a monthly or annual client has no quarters, and the same
 * grouping is right for them.
 */
export function groupByPeriod(jobs: Job[]): PeriodGroup[] {
  const groups = new Map<string, PeriodGroup>();
  for (const job of jobs) {
    // Runs from before the API sent dates fall back to the label, which still
    // groups them together even though it cannot order them.
    const end = job.period_end || job.period;
    const group = groups.get(end);
    if (group) {
      group.runs.push(job);
    } else {
      groups.set(end, { end, label: job.period || end, runs: [job] });
    }
  }

  // Sorted in place: these arrays were built here and nothing else holds a
  // reference to them.
  for (const group of groups.values()) {
    group.runs = group.runs.toSorted((a, b) => b.created_at - a.created_at);
  }

  return [...groups.values()].toSorted((a, b) => b.end.localeCompare(a.end));
}

/**
 * When a workpaper was produced.
 *
 * A finished job reports when it finished; one that failed or is still running
 * has only a request time, and saying "requested" rather than "generated" is
 * the difference between a timestamp and a misleading one.
 */
function whenOf(job: Job): { at: number; label: string } {
  return job.completed_at
    ? { at: job.completed_at, label: "Generated" }
    : { at: job.created_at, label: "Requested" };
}

/**
 * How long a running job has been going.
 *
 * Ticks on its own rather than off the poll, so the number moves every second
 * instead of jumping in threes. Mounted only while a job is unfinished, so
 * there is no timer running behind a finished workpaper.
 */
function Elapsed({ since }: { since: number }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  if (!since) return null;
  return <span className="muted">{formatElapsed(now / 1000 - since)}</span>;
}

function Timestamp({ job }: { job: Job }) {
  const { at, label } = whenOf(job);
  if (!at) return null;
  return (
    <span className="muted" title={`${label} ${formatWhenLong(at)}`}>
      {label} {formatWhen(at)}
    </span>
  );
}

export function GenerateWorkpaper({ client, admin }: { client: Client; admin: boolean }) {
  const auth = useAuth();
  const token = auth.user?.access_token;

  const [open, setOpen] = useState(false);
  const [job, setJob] = useState<Job | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState(lastCompletedQuarter);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState<string | null>(null);
  //: Which run's comparison is open, if any. One at a time, so the page does
  //: not fetch several ledgers nobody is reading.
  const [comparing, setComparing] = useState<string | null>(null);
  //: Periods whose earlier runs are showing. Keyed on the period end, so
  //: opening one quarter does not open the rest.
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  function toggleEarlier(end: string) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(end)) {
        next.delete(end);
      } else {
        next.add(end);
      }
      return next;
    });
  }
  const [history, setHistory] = useState<Job[] | null>(null);
  const timer = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  // Poll until the job reaches a terminal state, then stop. Leaving the timer
  // running would keep asking about every finished job on the page.
  //
  // A running review keeps it going: the review starts after the job is
  // already terminal, so stopping at is_terminal alone would leave the button
  // saying "Reviewing" until someone reloaded the page.
  useEffect(() => {
    const reviewRunning = job?.review?.status === "running";
    if (!job || (job.is_terminal && !reviewRunning) || !token) return;

    timer.current = window.setTimeout(() => {
      void (async () => {
        try {
          setJob(await getJob(token, job.job_id));
        } catch (err) {
          setError(err instanceof Error ? err.message : "Lost track of the job");
        }
      })();
    }, POLL_MS);

    return stopPolling;
  }, [job, token, stopPolling]);

  useEffect(() => stopPolling, [stopPolling]);

  const loadHistory = useCallback(async () => {
    if (!token) return;
    try {
      const past = await listWorkpapers(token, client.client_id);
      setHistory(past);
    } catch {
      // History is useful, not essential: a client that has never had a
      // workpaper generated is the common case on a new install.
      setHistory([]);
    }
  }, [token, client.client_id]);

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    void loadHistory();
  }, [loadHistory]);

  // A finished run belongs in the history, not only in this component's state.
  // Same reasoning as the mount effect: loadHistory only sets state after its
  // await, so there is no synchronous cascading render.
  useEffect(() => {
    if (!job?.is_terminal) return;
    // oxlint-disable-next-line react/set-state-in-effect
    void loadHistory();
  }, [job?.is_terminal, loadHistory]);

  async function start() {
    if (!token) return;
    setError(null);
    try {
      setJob(
        await startWorkpaper(token, {
          client_id: client.client_id,
          period_start: period.start,
          period_end: period.end,
          period_label: labelFor(period.start, period.end),
          basis: client.gst_basis,
        }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start generation");
    }
  }

  async function download(jobId: string, periodLabel: string) {
    if (!token) return;
    setDownloading(jobId);
    setError(null);
    try {
      // Fetched with the access token, so the download is authenticated like
      // every other call, then saved from memory. Nothing shareable is
      // produced and no URL lands in browser history.
      const { blob, filename } = await downloadWorkpaper(
        token,
        jobId,
        // Only used if the server's name cannot be read. Named after the
        // client either way, so two downloads never collide.
        `${periodLabel} BAS Workpaper - ${client.name}.xlsx`,
      );
      saveBlob(blob, filename);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not download the workpaper");
    } finally {
      setDownloading(null);
    }
  }

  async function remove(jobId: string) {
    if (!token) return;
    setDeleting(jobId);
    setError(null);
    try {
      await deleteWorkpaper(token, jobId);
      setConfirming(null);
      // Clear the panel if it was the run being shown, so a deleted workpaper
      // cannot be left on screen with a live Download button.
      setJob((current) => (current?.job_id === jobId ? null : current));
      await loadHistory();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete the workpaper");
    } finally {
      setDeleting(null);
    }
  }

  if (!client.xero.connected) {
    return <span className="muted">Connect Xero first</span>;
  }

  const past = (history ?? []).filter((h) => h.job_id !== job?.job_id);

  function deleteControls(entry: Job) {
    if (!admin) return null;
    if (confirming === entry.job_id) {
      return (
        <>
          <span className="muted">Delete?</span>
          <button
            className="link danger"
            disabled={deleting === entry.job_id}
            onClick={() => void remove(entry.job_id)}
          >
            {deleting === entry.job_id ? "Deleting…" : "Yes, delete"}
          </button>
          <button className="link" onClick={() => setConfirming(null)}>
            Keep
          </button>
        </>
      );
    }
    return (
      <button
        className="icon danger"
        aria-label={`Delete the ${entry.period} workpaper`}
        title="Delete this workpaper"
        onClick={() => setConfirming(entry.job_id)}
      >
        <TrashIcon />
      </button>
    );
  }

  async function runReview(entry: Job, force = false) {
    if (!token) return;
    // Re-running rebuilds the workbook, so anything a preparer has typed into
    // the verdict column goes with it. Worth asking before spending money and
    // losing work, rather than after.
    if (
      force &&
      !window.confirm(
        "This workpaper has already been reviewed. Running it again rebuilds " +
          "the workbook and discards any verdicts already entered. Continue?",
      )
    ) {
      return;
    }
    setError(null);
    setReviewing(entry.job_id);
    try {
      const updated = await startReview(token, entry.job_id, force);
      // Adopt the returned job so polling picks the review up immediately,
      // whether it was started from the history list or the current run.
      setJob(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start the review");
    } finally {
      setReviewing(null);
    }
  }

  /**
   * The comparison is opened deliberately rather than shown by default: it
   * reads two stored ledgers, and most of the time a preparer is downloading
   * a workpaper rather than asking what moved since the last run.
   */
  function diffControls(entry: Job) {
    if (entry.status !== "succeeded") return null;
    const showing = comparing === entry.job_id;
    return (
      <button className="link" onClick={() => setComparing(showing ? null : entry.job_id)}>
        {showing ? "Hide changes" : "What changed"}
      </button>
    );
  }

  function reviewControls(entry: Job) {
    if (entry.status !== "succeeded") return null;
    const review = entry.review;
    if (!review) return null;

    if (review.status === "running") {
      return (
        <>
          <span className="muted">{review.progress}…</span>
          <Elapsed since={entry.completed_at || entry.created_at} />
        </>
      );
    }
    if (review.status === "reviewed") {
      return (
        <>
          <span className="tag ok">
            {review.findings} finding{review.findings === 1 ? "" : "s"}
          </span>
          <span className="muted">of {review.lines.toLocaleString()} lines</span>
          <button className="link" onClick={() => void runReview(entry, true)}>
            Review again
          </button>
        </>
      );
    }
    return (
      <>
        <button
          className="link"
          disabled={reviewing === entry.job_id}
          onClick={() => void runReview(entry)}
        >
          {reviewing === entry.job_id ? "Starting…" : "Run GST review"}
        </button>
        {review.status === "failed" && review.error && (
          <span className="field-error">{review.error}</span>
        )}
      </>
    );
  }

  const runRow = (entry: Job) => (
        <li key={entry.job_id} className={entry.is_terminal ? "" : "working"}>
          {/* What this run is. The period is the group heading now, so the
              tag carries the outcome instead of repeating it. */}
          <div className="run">
            <span className={`tag ${entry.status === "failed" ? "bad" : "ok"}`}>
              {entry.status === "failed" ? "Failed" : "Generated"}
            </span>
            <Timestamp job={entry} />
            {!entry.is_terminal && <Elapsed since={entry.created_at} />}
            <span className="spacer" />
            {deleteControls(entry)}
          </div>

          {/* What you can do with it. Secondary actions sit below the run
              rather than beside it, which is what made the row unreadable
              once the review and the comparison both arrived. */}
          <div className="run-actions">
            {entry.status === "succeeded" && (
              <button
                className="link"
                disabled={downloading === entry.job_id}
                onClick={() => void download(entry.job_id, entry.period)}
              >
                {downloading === entry.job_id ? "Preparing…" : "Download"}
              </button>
            )}
            {reviewControls(entry)}
            {diffControls(entry)}
          </div>

          {comparing === entry.job_id && <WorkpaperDiff jobId={entry.job_id} />}
        </li>
  );

  const groups = groupByPeriod(past);
  const latest = groups[0];

  /**
   * The history, folded away.
   *
   * Every run of every quarter rendered in full inside each client's row,
   * and four clients with a few quarters each filled the page -- the list of
   * what has already been produced pushed the thing you came to do off the
   * screen.
   *
   * Two folds, because there are two kinds of noise. A client's whole
   * history hides behind one line naming its latest period, and within a
   * period only the newest run shows: the earlier ones are regenerations of
   * the same quarter, which matter when you are chasing one and not
   * otherwise. Native <details>, so it opens from the keyboard and holds its
   * own state.
   */
  const historyList = past.length > 0 && (
    <details className="history-fold">
      <summary>
        <span className="tag">{past.length}</span>
        {past.length === 1 ? "workpaper" : "workpapers"}
        {latest && (
          <span className="muted">
            {" · latest "}
            {latest.label}
          </span>
        )}
      </summary>

      <div className="periods">
        {groups.map((group) => {
          const [newest, ...earlier] = group.runs;
          const showingEarlier = expanded.has(group.end);
          return (
            <section key={group.end} className="period">
              <h4 className="period-head">
                {group.label}
                {earlier.length > 0 && (
                  <button
                    className="link"
                    aria-expanded={showingEarlier}
                    onClick={() => toggleEarlier(group.end)}
                  >
                    {showingEarlier
                      ? "hide earlier runs"
                      : `${earlier.length} earlier ${
                          earlier.length === 1 ? "run" : "runs"
                        }`}
                  </button>
                )}
              </h4>
              <ul className="history">
                {newest && runRow(newest)}
                {showingEarlier && earlier.map(runRow)}
              </ul>
            </section>
          );
        })}
      </div>
    </details>
  );

  if (job) {
    const tone = job.status === "failed" ? "bad" : job.is_terminal ? "ok" : "";
    return (
      <div className={job.is_terminal ? "job" : "job working"}>
        <span className={`tag ${tone}`}>{job.progress}</span>
        {!job.is_terminal && (
          <>
            <span className="muted"> {job.period}</span>
            <Elapsed since={job.created_at} />
          </>
        )}
        {job.status === "failed" && <div className="field-error">{job.error}</div>}
        {job.status === "succeeded" && (
          <>
            <button
              className="primary"
              disabled={downloading === job.job_id}
              onClick={() => void download(job.job_id, job.period)}
            >
              {downloading === job.job_id ? "Preparing…" : "Download"}
            </button>
            <div className="muted">
              {job.period} &middot; {String(job.stats?.exceptions ?? 0)} exception(s)
            </div>
            <div className="row">
              {reviewControls(job)}
              {diffControls(job)}
            </div>
            {comparing === job.job_id && <WorkpaperDiff jobId={job.job_id} />}
          </>
        )}
        {job.is_terminal && (
          <div className="row">
            <Timestamp job={job} />
            <span className="spacer" />
            {deleteControls(job)}
          </div>
        )}
        <button className="link" onClick={() => setJob(null)}>
          {job.is_terminal ? "Generate another" : "Hide"}
        </button>
        {error && <div className="field-error">{error}</div>}
        {historyList}
      </div>
    );
  }

  if (!open) {
    return (
      <div className="job">
        <button onClick={() => setOpen(true)}>Generate workpaper</button>
        {error && <div className="field-error">{error}</div>}
        {historyList}
      </div>
    );
  }

  return (
    <div className="job">
      <label>
        <span className="muted">From </span>
        <input
          type="date"
          aria-label="Period start"
          value={period.start}
          onChange={(e) => setPeriod({ ...period, start: e.target.value })}
        />
      </label>
      <label>
        <span className="muted"> to </span>
        <input
          type="date"
          aria-label="Period end"
          value={period.end}
          onChange={(e) => setPeriod({ ...period, end: e.target.value })}
        />
      </label>
      <span className="tag">{client.gst_basis} basis</span>
      {/* What the workpaper will be headed. Shown because it is derived from
          the dates, and seeing it is how a wrong one gets noticed. */}
      <span className="tag">{labelFor(period.start, period.end) || "—"}</span>
      <button className="primary" onClick={() => void start()}>
        Generate
      </button>
      <button className="link" onClick={() => setOpen(false)}>
        Cancel
      </button>
      {error && <div className="field-error">{error}</div>}
      {historyList}
    </div>
  );
}
